import React, { useMemo } from 'react';
import { Card, CardHeader, CardContent, Grid, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { AgeGroupChart, SexDistributionChart } from './DemographicComponents';
import {
  DemographicPatient,
  SEX_FEMALE,
  SEX_MALE,
  averageAge,
  sexDistribution,
} from '../utils/demographics';

interface DemographicStats {
  avgAge: string | null;
  malePercent: string;
  femalePercent: string;
}

interface DemographicsDashboardProps {
  data: DemographicPatient[];
  /**
   * The translated card title. Required, because ChartFactory resolves it from the config and
   * passes it unconditionally (`case 'demographic'`) — it is the single owner of title
   * resolution. This component simply had no prop to receive it, so it fell on the floor and
   * the card rendered a hardcoded English string on a French deployment. A `|| <Translate/>`
   * fallback here would be a second owner, and an unreachable one.
   */
  title: string;
}

const DemographicsDashboard: React.FC<DemographicsDashboardProps> = ({ data: patients, title }) => {
  const stats = useMemo<DemographicStats | null>(() => {
    if (!patients?.length) return null;

    const total = patients.length;
    const counts = sexDistribution(patients);
    const mean = averageAge(patients);

    return {
      // Null when no patient has a readable birthdate — the subheader then omits the figure
      // rather than printing "0.0 years" over a population whose age is simply not known.
      avgAge: mean === null ? null : mean.toFixed(1),
      malePercent: ((counts[SEX_MALE] / total) * 100).toFixed(1),
      femalePercent: ((counts[SEX_FEMALE] / total) * 100).toFixed(1),
    };
  }, [patients]);

  // An empty period is an answer, not an absence. Returning null here rendered an empty card
  // with no explanation, which reads as a chart that failed to load.
  if (!stats) {
    return (
      <Card>
        <CardHeader title={title} />
        <CardContent>
          <Typography variant="body2" color="textSecondary">
            <Translate id="hospital.analytics.demographics.empty" />
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={title}
        // "Total Patients: N" is deliberately absent: the age chart already carries the
        // population as its own total, and printing it twice let the two disagree while the
        // panel was fed from a different query than the tile above it (SHER-896).
        subheader={(
          <>
            {stats.avgAge !== null && (
              <>
                <Translate id="hospital.analytics.demographics.average_age" />
                {`: ${stats.avgAge} `}
                <Translate id="hospital.analytics.demographics.years" />
                {' | '}
              </>
            )}
            <Translate id="hospital.analytics.demographics.sex_distribution" />
            {`: ${stats.malePercent}% `}
            <Translate id="hospital.analytics.demographics.male" />
            {`, ${stats.femalePercent}% `}
            <Translate id="hospital.analytics.demographics.female" />
          </>
        )}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <AgeGroupChart data={patients} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SexDistributionChart data={patients} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default DemographicsDashboard;
